import React, {useContext, useEffect, useState, useMemo, useCallback} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faFolder, faSearch, faTrash, faUsers} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import {Modal, Tooltip} from 'antd';
import service from "../../utils/request";
import message from "../../utils/message";
import {SpaceContext} from "../../SpaceContext";

const permissionOptions = [
	{value: 'owner', label: 'Owner'},
	{value: 'read', label: 'Read'},
	{value: 'edit', label: 'Edit'},
];

const ManageAccessModal = ({isOpen, onClose, spaceName, spaceId}) => {
	const SpaceContextVal = useContext(SpaceContext);
	const [searchTerm, setSearchTerm] = useState('');
	const [users, setUsers] = useState([]);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [userSelected, setUserSelected] = useState(null);
	const [changeOwnerOpen, setChangeOwnerOpen] = useState(false);

	const userProfile = SpaceContextVal.userProfile;
	const allUsers = SpaceContextVal.users;

	useEffect(() => {
		if (spaceId) {
			if (spaceName === 'General' || spaceName === 'Legacy') {
				setUsers([{
					id: 'default-owner',
					name: 'Gerald',
					email: 'gerald@igarment.net',
					permission: 'owner'
				}]);
			} else {
				fetchSpaceUsers();
			}
		}
	}, [spaceId, spaceName]);

	const fetchSpaceUsers = async () => {
		try {
			const response = await service(`/space/space_users/${spaceId}`);
			setUsers(response);
		} catch (error) {
			console.error('Error fetching users:', error);
		}
	};

	const handlePermissionChange = async (userId, permission) => {
		try {
			if (permission.value === 'owner') {
				setUserSelected(users.find(u => u.id === userId));
				setChangeOwnerOpen(true);
				return;
			}

			await service.post('/space/update_user_permission', {
				space_id: spaceId,
				user_id: userId,
				permission: permission.value
			});

			message('success', 'Permission updated successfully');
			await fetchSpaceUsers();
		} catch (error) {
			console.error('Error updating permission:', error);
			message('error', error)
		}
	};

	const handleAddUser = async () => {
		if (!searchTerm.trim()) {
			message('error', 'Please enter an email address');
			return;
		}

		if (users.find(u => u.email === searchTerm)) {
			message('error', 'User already exists in this space');
			return;
		}

		try {
			const result = await service.post('/space/add_user', {
					space_id: spaceId,
					email: searchTerm,
					permission: 'read'
				}
			);
			setUsers(prevUsers => [...prevUsers, {
				id: result.user.id,
				name: result.user.name || '',
				email: result.user.email,
				permission: result.user.permission
			}]);
			setSearchTerm('');
			message('success', 'User added successfully');
		} catch (error) {
			message('error', error);
		}
	};

	const handleRemoveUser = async (user) => {
		setUserSelected(user);
		setConfirmDelete(true);
	};

	const confirmRemoveUser = async () => {
		try {
			await service.post('/space/remove_user', {
				space_id: spaceId,
				user_id: userSelected.id
			})

			message('success', 'User removed successfully');
			setUsers(users.filter(u => u.id !== userSelected.id));
			setConfirmDelete(false);
			setUserSelected(null);
		} catch (error) {
			console.error('Error removing user:', error);
			message('error', error);
		}
	};

	const handleSearch = (event) => {
		const {
			target
		} = event;

		setSearchTerm(target.value);
	};

	const changeOwner = async () => {
		try {
			await service.post('/space/change_owner', {
				email: userSelected.email,
				space_id: spaceId,
				user_id: userSelected.id
			});

			message('success', 'Owner changed successfully');
			window.location.reload();
		} catch (error) {
			message('error', error);
			setChangeOwnerOpen(false);
			setUserSelected(null);
		}
	}
	
	const spaceOwner = useMemo(() => {
		return users.find((user) => user.permission === 'owner');
	}, [users]);

	const filteredUsers = useMemo(() => {
		let result = [...users];

		result.sort((a, b) => {
			if (a.permission === 'owner' && b.permission !== 'owner') {
				return -1
			};
			
			if (b.permission === 'owner' && a.permission !== 'owner') {
				return 1
			};
			
			return a.name.localeCompare(b.name);
		});

		result = result.filter((user) => 
			user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.email.toLowerCase().includes(searchTerm.toLowerCase())
		)

		if (result.length === 0) {
			result = allUsers.map((user) => ({
				id: user.id,
				name: user.login_name,
				email: user.email,
				permission: null,
			}))
			.filter((user) => 
				user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				user.email.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}

		return result;
	}, [searchTerm, users, allUsers]);

	const isDisabled = useMemo(() => {
		if (!spaceOwner || !userProfile) {
			return true;
		}

		return spaceOwner.id !== userProfile.user_id;
	}, [spaceOwner, userProfile]);

	const handleClickAccess = (user) => {
		if (user.permission) {
			return;
		}

		setSearchTerm(user.email);
	};

	const getSortedPermission = (selectedPermission) => {
		let result = [...permissionOptions];

		result.sort((a, b) => (a.value === selectedPermission && b.value !== selectedPermission) ? -1 : 1);

		return result;
	};

	const getDisabledReason = (user) => {
		if (user.permission === 'owner') return "Owner permissions cannot be changed";
		if (user.id === userProfile.user_id) return "You cannot change your own permissions";
		if (isDisabled) return "Only the owner can change permissions";
		return "";
	};

	if (!isOpen) return null;

	return (
		<>
			<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
				<div className="bg-white rounded-lg p-7 shadow-lg sm:w-[50vw] sm:min-w-[450px] lg:w-[30vw] lg:min-w-[450px] lg:max-w-[500px]">
					<div className="flex flex-row justify-between items-center mb-3">
						<h2 className="text-xl font-medium text-left">
							Manage Access
						</h2>
						<button onClick={onClose} className="text-gray-500 hover:text-gray-700 pb-1">
							<span className="text-xl">
								x
							</span>
						</button>
					</div>
					<div className="flex flex-row items-center mb-5">
						<FontAwesomeIcon icon={faFolder} className="text-gray-500"/>
						<span className="text-base text-gray-600 ml-2">
							{spaceName}
						</span>
					</div>

					<div className="space-y-5">
						<div className="flex flex-col gap-4">
							<div className="flex relative">
								<FontAwesomeIcon
									icon={faSearch}
									className="absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2"
								/>
								<input
									type="email"
									placeholder="Add people by email"
									value={searchTerm}
									onChange={handleSearch}
									className="pl-9 flex-2 px-4 py-2 border rounded-md grow"
								/>
							</div>
						</div>

						<div className="mt-4 space-y-3 overflow-auto max-h-72">
							{
								filteredUsers.map(user => (
									<div 
										key={user.id} 
										className="flex items-center justify-between"
										onClick={() => handleClickAccess(user)}
									>
										<div className="flex items-center gap-3">
											<div
												className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-full"
											>
												{ user.name[0].toUpperCase() }
											</div>
											<div className="flex flex-col">
												<span className="text-sm font-medium text-left">
													{user.name}
												</span>
												<span className="text-xs text-gray-500 text-left">
													{user.email}
												</span>
											</div>
										</div>
										
										<div className="flex items-center">
											{
												user.permission &&
												<>
													{
														(user.permission === 'owner' || user.id === userProfile.user_id || isDisabled) && 
														<Tooltip title={getDisabledReason(user)} placement="right">
															<div>
																<Select
																	value={permissionOptions.find(opt => opt.value === user.permission)}
																	onChange={(permission) => handlePermissionChange(user.id, permission)}
																	options={getSortedPermission(user.permission)}
																	isDisabled={true}
																	className="w-32"
  																	classNamePrefix="select"
																	menuPosition="fixed"
																/>
															</div>
														</Tooltip>
													}
													{
														!(user.permission === 'owner' || user.id === userProfile.user_id || isDisabled) &&
														<Select
															value={permissionOptions.find(opt => opt.value === user.permission)}
															onChange={(permission) => handlePermissionChange(user.id, permission)}
															options={getSortedPermission(user.permission)}
															isDisabled={false}
															className="w-32"
															classNamePrefix="select"
															menuPosition="fixed"
														/>
													}
													<div 
														className={!isDisabled && !(filteredUsers.length === 1 && filteredUsers[0].permission === 'owner') ? 'w-[30px]' : ''}
													>
														{
															(user.permission !== 'owner' && user.id !== userProfile.user_id && !isDisabled) && 
															<button
																onClick={() => handleRemoveUser(user)}
																className="p-2 text-red-600 hover:text-red-800"
															>
																<FontAwesomeIcon icon={faTrash}/>
															</button>
														}
													</div>
												</>
											}
										</div>
									</div>
								))
							}
						</div>
						{	
							!isDisabled &&
							<button
								onClick={handleAddUser}
								className="w-full px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
							>
								Add User
							</button>
						}
						{
							isDisabled &&
							<Tooltip title="Only the owner can add users" placement="right">
								<button
									className="w-full px-4 py-2 text-white bg-blue-600 rounded-md cursor-not-allowed">
									Add User
								</button>
							</Tooltip>
						}
					</div>
				</div>
			</div>

			<Modal
				title="Change Owner"
				open={changeOwnerOpen}
				onOk={() => changeOwner()}
				onCancel={() => {
					setChangeOwnerOpen(false)
					setUserSelected(null);
				}}
				okText="Change Owner"
				okButtonProps={{danger: true}}
			>
				<p>Are you sure you want to change owner to {userSelected?.email}?
					You will be remove from this space and the new owner will have full control over this space.
				</p>
			</Modal>

			<Modal
				title="Remove User"
				open={confirmDelete}
				onOk={confirmRemoveUser}
				onCancel={() => {
					setConfirmDelete(false);
					setUserSelected(null);
				}}
				okText="Remove"
				okButtonProps={{danger: true}}
			>
				<p>Are you sure you want to remove {userSelected?.name} from this space?</p>
			</Modal>
		</>
	);
};

export default ManageAccessModal;
